import React, { useState } from "react";
import styled from "styled-components";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { formats } from "common/constants";
import provideUserFile from "common/ProvideFile";
import { YYYYMMDDHHmmssSSS } from "common/utils";

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 25px;

  .MuiTableContainer-root {
    border: 1px solid var(--table-border-color);
    border-radius: 5px;
    border-collapse: inherit;
    font-family: "AllianceNo2-Regular";
    color: var(--Text-Primary) !important;
    .MuiTableCell-root {
      font-size: 12px;
      line-height: 1.4;
      color: var(--Text-Primary) !important;
      border-bottom: none;
      padding: 9px 16px 10px 16px;
    }
    th.MuiTableCell-root {
      font-family: "GT-america-mono";
      font-size: var(--font-size, 12px);
      font-weight: normal;
      background-color: var(--table-header-background-color);
      border-bottom: 1px solid var(--table-border-color);
      vertical-align: middle;
      color: var(--text-primary);
    }
    tbody.MuiTableBody-root {
      tr.MuiTableRow-root:nth-of-type(odd) {
        background-color: var(--table-striped-background-color);
      }
    }
    tr.MuiTableRow-root {
    }
  }
`;

const ColorLabel = styled.div`
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`;

const WrapperButton = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 5px;
`;

const ExportButton = styled.div`
  display: inline-flex;
  align-items: center;

  font-family: "AllianceNo2-SemiBold" !important;
  font-weight: 100 !important;
  font-size: 11px !important;
  color: var(--Text-Primary) !important;
  .material-icons {
    line-height: 1.2em;
  }
  cursor: pointer;
`;

const Empty = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const isLightColor = (color) => {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 0 + 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
};

const getColor = (color) => {
  if (color === "transparent") return "#555";

  return isLightColor(color) ? "black" : "white";
};

const createCVSFilename = (prefix='active-flows-bqn') =>
  `${prefix}-${YYYYMMDDHHmmssSSS(new Date())}.csv`;

const asCSVContent = input =>
  input.split('\n').map( line => line.replaceAll(/\s+/g,',')).join('\n');

function CellContent({ column, value }) {
  const { type, precision, format, color, cellClassName, onClick } = column;
  if (!type) {
    return value;
  }
  if (type === "Text") {
    if (!onClick && !cellClassName) {
      return value;
    }
    if(!onClick && cellClassName){
      <div className={cellClassName}>
        {value}
      </div>
    }

    return (
      <div className={cellClassName} onClick={() => onClick(value)}>
        {value}
      </div>
    );
  }
  if (type === "NumberOrNA") {
    if (format) {
      return format(value);
    }
    if (value === "n/a") {
      return "n/a";
    }
    return parseFloat(value);
  }
  if (type === "Time") {
    if (value === null) {
      return "n/a";
    }
    return value;
  }

  if (type === "TextColor") {
    if (value == undefined) {
      return null;
    }
    if (value === null) {
      return "n/a";
    }

    return (
      <ColorLabel
        className="color-label"
        color={getColor(color(value))}
        backgroundColor={color(value)}
      >
        {value}
      </ColorLabel>
    );
  }

  if (type === "NumberColor") {
    if (value == undefined) {
      return null;
    }
    if (value === null) {
      return "n/a";
    }

    return (
      <ColorLabel
        className="color-label-number"
        color={getColor(color(value))}
        backgroundColor={color(value)}
      >
        {precision ? formats[precision](parseFloat(value)) : parseFloat(value)}
      </ColorLabel>
    );
  }

  if (type === "Number") {
    if (precision) {
      return formats[precision](parseFloat(value));
    }
    return parseFloat(value);
  }

  return value;
}

const paginationModel = { page: 0, pageSize: 5 };

function MuiTable({ columns, rows, showPagination = false, exportCSV=true }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);

  if(!rows || rows.length===0){
    return <Wrapper><Empty>"No Data Available"</Empty></Wrapper>
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleCSVDownload = () => {
    const headArray = columns.reduce((acc, column)=>{
      acc.push(column.label)
      return acc;
    },[]).join('    ');
    const rowArrays = rows.reduce((acc, row) => {
      const newRow = columns.reduce((accRow, column)=>{
      const value = row[column.id] || 'n/a';
      accRow.push(value)
      return accRow;
      }, [])
      acc.push(newRow.join('    '));
      return acc;
    }, []);
    const content = [headArray, ...rowArrays].join('\n');
    provideUserFile(createCVSFilename(), asCSVContent(content));
  };

  return (
    <Wrapper>
      {exportCSV ? (
        <WrapperButton>
          <ExportButton onClick={handleCSVDownload}>
            <span>Export:</span>
            <i className="material-icons">file_download</i>
          </ExportButton>
        </WrapperButton>
      ) : null}
      <TableContainer sx={{ maxHeight: 750 }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          <CellContent column={column} value={value} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination ? (
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </Wrapper>
  );
}
export default MuiTable;
